import type { PayloadAction } from '@reduxjs/toolkit';
import { extInfo } from 'lib/extInfo';
import { logger } from 'lib/logger';
import { IS_BIZ_USER, IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import { enableGalleryFastscroll } from 'reactApp/appHelpers/featuresHelpers/galleryFastscroll';
import { CAMERA_UPLOADS_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import {
    type GalleryAnchorRequest,
    type GalleryListRequest,
    requestAnchorsList,
    requestGalleryList,
} from 'reactApp/modules/gallery/gallery.helpers';
import {
    changeGalleryFolder,
    galleryAnchorsLoadSuccess,
    galleryAnchorsRequest,
    galleryLoadAnchorListRequest,
    galleryLoadAnchorListSuccess,
    galleryLoadAnchorPaginatedListSuccess,
    galleryLoadCategoryRequest,
    galleryLoadMoreRequest,
    galleryLoadMoreSuccess,
    galleryLoadSuccess,
} from 'reactApp/modules/gallery/gallery.module';
import { routeChangeStart } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import type { GetStateResponse } from 'reactApp/modules/router/router.types';
import { ESortOder } from 'reactApp/modules/sort/sort.types';
import { getLoadMoreLimit } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { call, put, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';

import {
    getCurrentGalleryCategory,
    getGalleryAnchorById,
    getGalleryCategory,
    getGalleryCursorByCategory,
    getGalleryFolder,
    isGalleryCategoryLoaded,
} from './gallery.selectors';
import {
    type ChangeGalleryFolderAction,
    type GalleryAnchor,
    type GalleryAnchorsLoadRequestAction,
    type GalleryCategory,
    type GalleryLoadCategoryRequestAction,
    GalleryCategoryType,
} from './gallery.types';

const LOAD_LIMIT = getLoadMoreLimit(EStorageType.gallery);

function* fetchGalleryList({
    category = null,
    folder,
    storage,
    mtime_from,
    mtime_to,
    order,
    cursor,
    limit = LOAD_LIMIT,
}: {
    category: GalleryCategoryType | null;
    folder?: string;
    storage?: EStorageType;
    mtime_from?: number;
    mtime_to?: number;
    order?: ESortOder;
    limit?: number;
    cursor?: string;
}) {
    try {
        const eitherBizOrOnPremiseUser = IS_BIZ_USER || IS_ONPREMISE;

        if (!category) {
            category = yield select(getGalleryCategory);
        }

        /**
         * storage перелающийся извне - вынужденная мера, так как при переходе из альбомов
         * в галерею galleryLoadRequest пушится раньше, чем обрабатывается установка getCurrentStorage
         * и в итоге внутри галереи мы начинали грузить вместо экстеншенов галереи экстеншены альбома
         * что было не неверно.
         * Экшн galleryLoadRequest диспатчится только из новой галереи, поэтом мы можем четко сказать
         * что здесь сторадж будет задан значение EStorageType.gallery
         */
        const currentStorage = storage || (yield select(getCurrentStorage));

        const categoryCursor = yield select(getGalleryCursorByCategory, category as GalleryCategoryType);
        const extensions =
            currentStorage === EStorageType.albums
                ? extInfo.getExtsForAlbums()?.join(',')
                : currentStorage === EStorageType.vigbo
                ? extInfo.getExtsForVigboAlbums()?.join(',')
                : extInfo.getExtsForGallery(eitherBizOrOnPremiseUser && category)?.join(',');

        const data: GalleryListRequest = {
            extensions,
            cursor: cursor || categoryCursor,
            limit,
            mtime_from: mtime_from ? mtime_from / 1000 : undefined,
            mtime_to: mtime_to ? mtime_to / 1000 : undefined,
            order,
        };

        if (!eitherBizOrOnPremiseUser) {
            if (category === GalleryCategoryType.mobile) {
                data.include = [CAMERA_UPLOADS_FOLDER_ID];
            } else if (category === GalleryCategoryType.desktop) {
                data.exclude = [CAMERA_UPLOADS_FOLDER_ID];
            }
        }

        if (folder) {
            data.include = [folder];
        }

        return yield call(requestGalleryList, data);
    } catch (error) {
        logger.error(error);
    }
}

function* fetchGalleryAnchors({ category }: { category: GalleryCategoryType | null }) {
    try {
        if (!category) {
            category = yield select(getGalleryCategory);
        }

        const extensions = extInfo.getExtsForGallery().join(',');

        const data: GalleryAnchorRequest = {
            extensions,
            // eslint-disable-next-line new-cap
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        if (category === GalleryCategoryType.mobile) {
            data.include = [CAMERA_UPLOADS_FOLDER_ID];
        } else if (category === GalleryCategoryType.desktop) {
            data.exclude = [CAMERA_UPLOADS_FOLDER_ID];
        }

        return yield call(requestAnchorsList, data);
    } catch (error) {
        logger.error(error);
    }
}

function* fetchGallery({
    category = GalleryCategoryType.all,
    folder,
    mtime_from,
    mtime_to,
}: {
    category: GalleryCategoryType;
    folder?: string;
    mtime_from?: number;
    mtime_to?: number;
}) {
    if (enableGalleryFastscroll) {
        yield fetchGalleryAnchors({ category });
    }

    return yield fetchGalleryList({ category, mtime_from, mtime_to, folder });
}

function* loadGalleryOnStart(action: PayloadAction<GetStateResponse>) {
    const storage = action.payload.storage;

    const isLoaded = yield select(isGalleryCategoryLoaded, GalleryCategoryType.all);

    if (storage !== EStorageType.gallery || (storage === EStorageType.gallery && isLoaded)) {
        return;
    }

    const data = yield fetchGalleryList({ category: GalleryCategoryType.all });
    yield put(galleryLoadSuccess({ category: GalleryCategoryType.all, data }));
}

function* loadAnchorList(action: ReturnType<typeof galleryLoadAnchorListRequest>) {
    const { anchorId } = action.payload;
    const requestedAnchor: GalleryAnchor | undefined = yield select(getGalleryAnchorById, anchorId);
    if (!requestedAnchor) {
        return;
    }

    const category: GalleryCategory = yield select(getCurrentGalleryCategory);
    const orderedAnchorsId = category.anchors;
    const currentAnchorIndex = orderedAnchorsId.indexOf(requestedAnchor.id);

    const loadedAnchorIds: string[] = [];
    /** Начальное значение -requestedAnchor.count, так как оно учитывается дважды */
    let itemsRequested = -requestedAnchor.count;

    /** Итоговый диапазон дат загрузки для нескольких якорей */
    let date_from = 0;
    let date_to = 0;

    for (let i = currentAnchorIndex; i < orderedAnchorsId.length; i++) {
        const anchor: GalleryAnchor = yield select(getGalleryAnchorById, orderedAnchorsId[i]);

        if (anchor.isLoaded) {
            /** Если фото из якоря загружены, то просто увеличиваем кол-во элементов */
            itemsRequested += anchor.count;
        } else if (anchor.cursor) {
            /** Если у якоря есть курсор, то заправшиваем данные из него и перестаем набирать якоря для запроса */
            const { galleryListV4 } = yield fetchGalleryList({
                category: category.id,
                order: ESortOder.desc,
                mtime_from: anchor.periodStart,
                mtime_to: anchor.periodEnd,
                cursor: anchor.cursor,
                limit: LOAD_LIMIT / 2,
            });

            yield put(galleryLoadAnchorPaginatedListSuccess({ anchorId: anchor.id, cursorOrder: ESortOder.desc, data: galleryListV4 }));

            break;
        } else if (itemsRequested + anchor.count > LOAD_LIMIT / 2) {
            /**
             * Если уже запросили достаточно фото, то перестаем запрашивать
             * Если якорь большой, то запрашиваем с пагинацией
             */
            if (anchor.count > LOAD_LIMIT) {
                const { galleryListV4 } = yield fetchGalleryList({
                    category: category.id,
                    order: ESortOder.asc,
                    mtime_from: anchor.periodStart,
                    mtime_to: anchor.periodEnd,
                    limit: LOAD_LIMIT / 2,
                });

                yield put(galleryLoadAnchorPaginatedListSuccess({ anchorId: anchor.id, cursorOrder: ESortOder.asc, data: galleryListV4 }));
            }

            break;
        } else {
            /** Иначе просто увеличиваем кол-во запрашиваемых фото и диапазон дат */
            itemsRequested += anchor.count;

            date_from = anchor.periodStart;
            loadedAnchorIds.push(anchor.id);
        }
    }

    for (let i = currentAnchorIndex; i >= 0; i--) {
        const anchor: GalleryAnchor = yield select(getGalleryAnchorById, orderedAnchorsId[i]);

        if (anchor.isLoaded) {
            /** Если фото из якоря загружены, то просто увеличиваем кол-во элементов */
            itemsRequested += anchor.count;
        } else if (anchor.cursor) {
            /** Если у якоря есть курсор, то заправшиваем данные из него и перестаем набирать якоря для запроса */
            const { galleryListV4 } = yield fetchGalleryList({
                category: category.id,
                order: ESortOder.desc,
                mtime_from: anchor.periodStart,
                mtime_to: anchor.periodEnd,
                cursor: anchor.cursor,
                limit: LOAD_LIMIT / 2,
            });

            yield put(galleryLoadAnchorPaginatedListSuccess({ anchorId: anchor.id, cursorOrder: ESortOder.desc, data: galleryListV4 }));

            break;
        } else if (itemsRequested + anchor.count > LOAD_LIMIT) {
            /**
             * Если уже запросили достаточно фото, то перестаем запрашивать
             * Если якорь большой, то запрашиваем с пагинацией
             */
            if (anchor.count > LOAD_LIMIT) {
                const { galleryListV4 } = yield fetchGalleryList({
                    category: category.id,
                    order: ESortOder.asc,
                    mtime_from: anchor.periodStart,
                    mtime_to: anchor.periodEnd,
                    limit: LOAD_LIMIT / 2,
                });

                yield put(galleryLoadAnchorPaginatedListSuccess({ anchorId: anchor.id, cursorOrder: ESortOder.asc, data: galleryListV4 }));
            }

            break;
        } else {
            /** Иначе просто увеличиваем кол-во запрашиваемых фото и диапазон дат */
            itemsRequested += anchor.count;

            date_to = anchor.periodEnd;
            loadedAnchorIds.push(anchor.id);
        }
    }

    if (date_from === 0 || date_to === 0) {
        return;
    }

    const { galleryListV4 } = yield fetchGalleryList({ category: category.id, mtime_from: date_from, mtime_to: date_to });
    yield put(galleryLoadAnchorListSuccess({ loadedAnchorIds, data: galleryListV4 }));
}

function* loadMoreGallery() {
    const folder = yield select(getGalleryFolder);
    const data = yield fetchGalleryList({ category: null, folder });
    yield put(galleryLoadMoreSuccess({ data, folder, isExactFolder: Boolean(folder) }));
}

function* loadAnchors(action: PayloadAction<GalleryAnchorsLoadRequestAction>) {
    const { category } = action.payload;
    const { anchors } = yield fetchGalleryAnchors({ category });
    yield put(galleryAnchorsLoadSuccess({ anchors }));
}

function* loadGalleryCategory(action: PayloadAction<GalleryLoadCategoryRequestAction>) {
    const { category } = action.payload;
    const data = yield fetchGallery({ category });
    yield put(galleryLoadSuccess({ category, data }));
}

function* loadGalleryFolder(action: PayloadAction<ChangeGalleryFolderAction>) {
    const { folder, category = GalleryCategoryType.all } = action.payload;
    const data = yield fetchGalleryList({ category, folder, cursor: '' });
    yield put(galleryLoadSuccess({ category, data, folder, isExactFolder: true }));
}

export function* watchGallery() {
    yield takeEvery(routeChangeStart.toString(), loadGalleryOnStart);

    yield takeLatest(galleryLoadMoreRequest.toString(), loadMoreGallery);
    yield takeEvery(galleryAnchorsRequest.toString(), loadAnchors);

    yield takeLeading(galleryLoadAnchorListRequest.toString(), loadAnchorList);

    yield takeEvery(galleryLoadCategoryRequest.toString(), loadGalleryCategory);
    yield takeEvery(changeGalleryFolder.toString(), loadGalleryFolder);
}
